import React, { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import "./header.css";

const API_BASE_URL =
  process.env.NODE_ENV === "production"
    ? process.env.REACT_APP_API_URL_PROD // https://demenagementsamyyhaul.ca/api
    : process.env.REACT_APP_API_URL_DEV; // http://158.69.205.231:3000


const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [company, setCompany] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [logoError, setLogoError] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();

  // Fonction pour récupérer l'utilisateur actuel
  const getCurrentUser = () => {
    const authToken =
      localStorage.getItem("authToken") || localStorage.getItem("token");
    const userData = localStorage.getItem("currentUser");

    if (authToken && userData) {
      try {
        const user = JSON.parse(userData);
        return user;
      } catch (error) {
        console.error("Erreur parsing user data:", error);
        // Nettoyer les données corrompues
        localStorage.removeItem("authToken");
        localStorage.removeItem("token");
        localStorage.removeItem("currentUser");
        return null;
      }
    }
    return null;
  };

  // Charger infos user depuis localStorage
  useEffect(() => {
    const checkUserAuth = () => {
      const user = getCurrentUser();
      setCurrentUser(user);
    };

    // Vérifier immédiatement au chargement
    checkUserAuth();

    // Écouter les événements de changement d'authentification
    window.addEventListener("userAuthChanged", checkUserAuth);
    window.addEventListener("storage", checkUserAuth);

    return () => {
      window.removeEventListener("userAuthChanged", checkUserAuth);
      window.removeEventListener("storage", checkUserAuth);
    };
  }, []);

  // Réagir aux changements de route
  useEffect(() => {
    const user = getCurrentUser();
    setCurrentUser(user);
  }, [location.pathname]);

  // Fonction pour déclencher une mise à jour immédiate de l'authentification
  const triggerAuthUpdate = () => {
    const user = getCurrentUser();
    setCurrentUser(user);
  };

  // Fermer le menu utilisateur quand on clique ailleurs
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest(".user-menu-container")) {
        setIsUserMenuOpen(false);
      }
    };

    if (isUserMenuOpen) {
      document.addEventListener("click", handleClickOutside);
    }

    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, [isUserMenuOpen]);

  // Fetch company config
  useEffect(() => {
    fetch(`${API_BASE_URL}/company-config/latest`)
      .then((res) => {
        if (!res.ok) {
          throw new Error(`HTTP error! status: ${res.status}`);
        }
        return res.json();
      })
      .then((data) => {
        console.log("Company config reçu:", data);

        const updatedData = {
          ...data,
          logo: data.logo?.includes("example.com")
            ? "/uploads/company-logos/logo-removebg-preview (1).png"
            : data.logo,
        };

        setCompany(updatedData);
        setLogoError(false);
      })
      .catch((err) => {
        console.error("Erreur récupération company-config:", err);
      });
  }, []);

  // Scroll effect
  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const toggleMenu = () => setIsMenuOpen(!isMenuOpen);
  const closeMenu = () => setIsMenuOpen(false);
  const toggleUserMenu = () => setIsUserMenuOpen(!isUserMenuOpen);

  const handleNavClick = (href) => {
    if (href.startsWith("#")) {
      const element = document.querySelector(href);
      if (element) {
        element.scrollIntoView({ behavior: "smooth" });
      }
    }
    closeMenu();
  };

  const handleLogout = () => {
    // Supprimer tous les tokens possibles
    localStorage.removeItem("authToken");
    localStorage.removeItem("token");
    localStorage.removeItem("currentUser");

    setCurrentUser(null);
    setIsUserMenuOpen(false);

    // Déclencher l'événement de changement d'auth
    window.dispatchEvent(new Event("userAuthChanged"));

    // Forcer une mise à jour immédiate
    triggerAuthUpdate();

    // Rediriger vers l'accueil
    navigate("/");
  };

  const navigateToProfile = () => {
    setIsUserMenuOpen(false);

    // Naviguer selon le rôle
    if (currentUser?.role === "ADMIN" || currentUser?.role === "admin") {
      navigate("/admin");
    } else {
      navigate("/user");
    }
  };

  const getUserInitials = (firstName, lastName) => {
    const first = firstName || "";
    const last = lastName || "";

    if (first && last) {
      return (first[0] + last[0]).toUpperCase();
    } else if (first) {
      return first[0].toUpperCase();
    } else if (currentUser?.username) {
      return currentUser.username.substring(0, 2).toUpperCase();
    }
    return "?";
  };

  const getUserDisplayName = () => {
    if (currentUser?.firstName && currentUser?.lastName) {
      return `${currentUser.firstName} ${currentUser.lastName}`;
    } else if (currentUser?.fullName) {
      return currentUser.fullName;
    } else if (currentUser?.username) {
      return currentUser.username;
    }
    return "Utilisateur";
  };

  const getRoleLabel = () => {
    const role = currentUser?.role?.toLowerCase();
    return role === "admin" ? "Administrateur" : "Utilisateur";
  };

  const handleImageError = (e) => {
    console.error("Erreur de chargement du logo:", e.target.src);
    console.error("Logo path original:", company?.logo);
    setLogoError(true);
  };

  const handleImageLoad = () => {
    console.log("Logo chargé avec succès:", company?.logo);
    setLogoError(false);
  };

  return (
    <header className={`header-container ${isScrolled ? "scrolled" : ""}`}>
      <div className="header-content">
        <div className="header-main">
          <a
            href="/"
            className="flex items-center space-x-3 hover:opacity-120 transition"
          >
            {company?.logo && (
              <img
                src={
                  company.logo?.startsWith("http")
                    ? company.logo
                    : `${API_BASE_URL}${company.logo || "/default-logo.png"}`
                }
                alt={`Logo de ${company?.name || "l'entreprise"}`}
                className="w-20 h-20 object-contain rounded-md"
              />
            )}
            <span className="text-2xl sm:text-4xl font-bold text-red-600">
              {company?.name || "Chargement..."}
            </span>
          </a>

          {/* Navigation Desktop */}
          <div className="desktop-nav">
            <div className="nav-links">
              <Link
                to="/"
                className="nav-link"
                onClick={(e) => {
                  closeMenu();

                  if (location.pathname === "/") {
                    // Si déjà sur la page d'accueil → juste scroller en haut
                    e.preventDefault();
                    window.scrollTo({ top: 0, behavior: "smooth" });
                  }
                  // Sinon, laisse React Router faire la navigation normalement
                }}
              >
                Accueil
              </Link>

              <a
                href="#services"
                className="nav-link"
                onClick={(e) => {
                  e.preventDefault();
                  closeMenu();
                  if (location.pathname === "/") {
                    document
                      .querySelector("#services")
                      ?.scrollIntoView({ behavior: "smooth" });
                  } else {
                    navigate("/"); // naviguer vers l'accueil
                    // Puis attendre que la page se charge et scroller
                    setTimeout(() => {
                      document
                        .querySelector("#services")
                        ?.scrollIntoView({ behavior: "smooth" });
                    }, 100);
                  }
                }}
              >
                Services
              </a>
              <a
                href="#avantages"
                className="nav-link"
                onClick={(e) => {
                  e.preventDefault();
                  closeMenu();
                  if (location.pathname === "/") {
                    document
                      .querySelector("#avantages")
                      ?.scrollIntoView({ behavior: "smooth" });
                  } else {
                    navigate("/");
                    setTimeout(() => {
                      document
                        .querySelector("#avantages")
                        ?.scrollIntoView({ behavior: "smooth" });
                    }, 100);
                  }
                }}
              >
                Avantages
              </a>

              <a
                href="#contact"
                className="nav-link"
                onClick={(e) => {
                  e.preventDefault();
                  closeMenu();
                  if (location.pathname === "/") {
                    document
                      .querySelector("#contact")
                      ?.scrollIntoView({ behavior: "smooth" });
                  } else {
                    navigate("/");
                    setTimeout(() => {
                      document
                        .querySelector("#contact")
                        ?.scrollIntoView({ behavior: "smooth" });
                    }, 100);
                  }
                }}
              >
                Contact
              </a>
            </div>

            <div className="cta-buttons">
              <a
                href="#services"
                onClick={(e) => {
                  e.preventDefault();
                  handleNavClick("#services");
                }}
                className="cta-button primary"
              >
                <span className="button-text">Devis Gratuit</span>
              </a>

              {/* Menu utilisateur ou bouton Login */}
              {currentUser ? (
                <div className="user-menu-container">
                  <button
                    className="user-menu-trigger"
                    onClick={toggleUserMenu}
                  >
                    <div className="user-avatar">
                      {getUserInitials(
                        currentUser.firstName,
                        currentUser.lastName
                      )}
                    </div>
                    <span className="user-name">{getUserDisplayName()}</span>
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      className={`dropdown-arrow ${
                        isUserMenuOpen ? "open" : ""
                      }`}
                    >
                      <polyline points="6,9 12,15 18,9"></polyline>
                    </svg>
                  </button>

                  {/* Menu déroulant utilisateur */}
                  <div
                    className={`user-dropdown ${isUserMenuOpen ? "open" : ""}`}
                  >
                    {/* En-tête du menu */}
                    <div className="user-dropdown-header">
                      <div className="user-display-name">
                        {getUserDisplayName()}
                      </div>
                      <div className="user-role">{getRoleLabel()}</div>
                      {currentUser.email && (
                        <div className="user-email">{currentUser.email}</div>
                      )}
                    </div>

                    {/* Options du menu */}
                    <div className="user-dropdown-options">
                      <button
                        onClick={navigateToProfile}
                        className="dropdown-option"
                      >
                        <span className="option-icon">👨‍💼</span>
                        <span>
                          Mon{" "}
                          {currentUser?.role?.toLowerCase() === "admin"
                            ? "tableau de bord"
                            : "profil"}
                        </span>
                      </button>

                      <div className="dropdown-divider"></div>

                      <button
                        onClick={handleLogout}
                        className="dropdown-option logout-option"
                      >
                        <span className="option-icon">🚪</span>
                        <span>Se déconnecter</span>
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                <Link to="/login" className="cta-button secondary">
                  Connexion
                </Link>
              )}
            </div>
          </div>

          {/* Mobile Menu Button */}
          <button
            className={`mobile-menu-btn ${isMenuOpen ? "open" : ""}`}
            onClick={toggleMenu}
            aria-label="Toggle menu"
          >
            <div className="hamburger">
              <span></span>
              <span></span>
              <span></span>
            </div>
          </button>
        </div>

        {/* Mobile Menu */}
        <div className={`mobile-menu ${isMenuOpen ? "open" : ""}`}>
          <div className="mobile-menu-content">
            <Link to="/" onClick={closeMenu}>
              🏠 Accueil
            </Link>
            <a
              href="#services"
              onClick={(e) => {
                e.preventDefault();
                handleNavClick("#services");
              }}
            >
              🔧 Services
            </a>
            <a
              href="#avantages"
              onClick={(e) => {
                e.preventDefault();
                handleNavClick("#avantages");
              }}
            >
              ⭐ Avantages
            </a>
            <a
              href="#contact"
              onClick={(e) => {
                e.preventDefault();
                handleNavClick("#contact");
              }}
            >
              📞 Contact
            </a>
            <a
              href="#services"
              onClick={(e) => {
                e.preventDefault();
                handleNavClick("#services");
                closeMenu();
              }}
              className="mobile-cta-button"
            >
              📋 Devis Gratuit
            </a>

            {currentUser ? (
              <>
                <button
                  onClick={() => {
                    navigateToProfile();
                    closeMenu();
                  }}
                  className="mobile-cta-button"
                >
                  👨‍💼 Profil
                </button>
                <button
                  onClick={() => {
                    handleLogout();
                    closeMenu();
                  }}
                  className="mobile-cta-button logout"
                >
                  🚪 Se déconnecter
                </button>
              </>
            ) : (
              <Link
                to="/login"
                onClick={closeMenu}
                className="mobile-cta-button"
              >
                🔐 Connexion
              </Link>
            )}
          </div>
        </div>
      </div>

      {/* Overlay */}
      {isMenuOpen && <div className="menu-overlay" onClick={closeMenu}></div>}
    </header>
  );
};

export default Header;
